import { when, is, objOf, pipe, set, lensPath, dissocPath } from 'ramda'
import { RSAA } from 'redux-api-middleware'

import { Middleware, MiddlewareAPI, ReduxAction, ActionType } from './types'

/** CacheSubscriber is a type that represents the shape of a cache subscriber */
export interface CacheSubscriber {
  dispatch: (action: any) => any
  successType: string
  successTypeMeta?: Record<string, any>
  errorType: string
  errorTypeMeta?: Record<string, any>
}

/** PendingRequest is a type that represents the shape of a pending request */
export interface PendingRequest {
  subscribers: CacheSubscriber[]
}

/**
 * Creates a cache middleware system with encapsulated state
 *
 * This factory function returns a set of middleware and utilities that work
 * together to cache API responses and handle duplicate requests. The cache
 * state is properly encapsulated within the closure.
 *
 * @returns {Object} An object containing the cache middleware system
 * @returns {Middleware} ReadCache - Middleware for reading from cache and
 *   preventing duplicates
 * @returns {Middleware} RecordCache - Middleware for recording cache results
 *   and notifying subscribers
 * @returns {Function} ClearApiCache - Function to clear all cached data and
 *   pending requests
 */
export const createCacheMiddleware = () => {
  // Encapsulated cache storage - only accessible within this closure
  const cachedResult: Record<string, any> = {}
  const pendingRequests: Record<string, PendingRequest> = {}

  /**
   * Middleware that caches API responses and handles duplicate requests
   *
   * This middleware intercepts GET requests marked with `cache: true` and:
   *
   * 1. Returns cached data if available
   * 2. Prevents duplicate API calls by tracking in-flight requests
   * 3. Notifies all subscribers when the request completes
   */
  const readCache: Middleware =
    ({ dispatch, getState }: MiddlewareAPI) =>
    (next: (action: any) => any) =>
    (action: ReduxAction) => {
      const fluxAction = action[RSAA]

      if (fluxAction && fluxAction.method === 'GET' && fluxAction.cache) {
        const { endpoint: rawEndpoint } = fluxAction
        const maybeSuccessType = fluxAction.types[1]
        const maybeErrorType = fluxAction.types[2]

        // the `maybeSuccessType` could be object or string,
        // turning the actionType string into object to match format.
        const { type: successType, meta: successTypeMeta } = when(
          is(String),
          objOf('type'),
        )(maybeSuccessType) as ActionType

        // the `maybeErrorType` could be object or string,
        // turning the actionType string into object to match format.
        const { type: errorType, meta: errorTypeMeta } = when(
          is(String),
          objOf('type'),
        )(maybeErrorType) as ActionType

        const endpoint: string =
          typeof rawEndpoint === 'function'
            ? rawEndpoint(getState())
            : rawEndpoint

        // Check if we have cached data. Uses `in` to avoid being just
        // a simple truthy check to accurately detect cached entries
        if (endpoint in cachedResult) {
          return dispatch({
            type: successType,
            meta: successTypeMeta,
            payload: cachedResult[endpoint],
          })
        }

        // Check if request is already in flight to prevent race conditions
        // where multiple identical requests could result in duplicate API calls
        if (pendingRequests[endpoint]) {
          // Add this dispatch to the pending request's subscribers
          pendingRequests[endpoint].subscribers.push({
            dispatch,
            successType,
            successTypeMeta,
            errorType,
            errorTypeMeta,
          })
          // Don't proceed with another API call
          return
        }

        // Mark request as pending and track the original caller
        pendingRequests[endpoint] = {
          subscribers: [
            {
              dispatch,
              successType,
              successTypeMeta,
              errorType,
              errorTypeMeta,
            },
          ],
        }

        // This block below uses Ramda to modify the action that gets
        // returned from this middleware. Essentially it modifies the
        // *_SUCCESS type from the RSAA so that it includes any original
        // meta info, but adds the `cache` and `cacheKey` values. This will
        // be used later by the `recordCache` middleware to know whether this
        // response was supposed to be cached. It also removes the cache from
        // the main RSAA action so that it doesn't get passed through to the
        // next middleware.
        //
        // Original action was probably something like:
        // {
        //   type: 'GET_USER_SUCCESS'
        // }
        //
        // New action is now:
        // {
        //   type: 'GET_USER_SUCCESS',
        //   meta: {
        //     cache: true,
        //     cacheKey: '/api/users/1',
        //   },
        // }
        const nextAction = pipe(
          set(lensPath([RSAA, 'types', 1]), {
            type: successType,
            meta: {
              ...successTypeMeta,
              cache: true,
              cacheKey: endpoint,
            },
          }),
          dissocPath([RSAA, 'cache']),
        )(action)

        return next(nextAction)
      }

      // make sure that `cache` doesn't get through or else
      // redux-api-middleware will barf on you. Don't just mutate
      // the action though, just return a new one.
      let nextAction = action
      if (fluxAction && action[RSAA]) {
        nextAction = dissocPath([RSAA, 'cache'])(action)
      }

      return next(nextAction)
    }

  /**
   * This middleware is used to record the cache data. It will be called after
   * the API call is made.
   *
   * Handles both success and error responses:
   *
   * - On success: caches the result and notifies all pending subscribers
   * - On error: notifies all pending subscribers with the error and clears
   *   pending requests
   *
   * @param {Object} action - The Redux action being processed
   * @param {Object} action.meta - The meta object containing cache information
   * @param {boolean} action.meta.cache - Whether this action should be cached
   * @param {string} action.meta.cacheKey - The key to use for caching
   * @param {any} action.payload - The data to be cached (success) or error
   *   details (error)
   * @param {boolean} action.error - Whether this action represents an error
   *   response
   * @returns {Object} The next action in the middleware chain
   */
  const recordCache: Middleware =
    () => (next: (action: any) => any) => (action: ReduxAction) => {
      const { meta: { cache, cacheKey } = {}, error } = action

      if (cache && !!cacheKey) {
        if (pendingRequests[cacheKey]) {
          const { subscribers } = pendingRequests[cacheKey]

          if (error) {
            // Handle error case - notify all subscribers with the error action
            subscribers.forEach(({ dispatch, errorType, errorTypeMeta }) => {
              dispatch({
                type: errorType,
                meta: errorTypeMeta,
                payload: action.payload,
                error: true,
              })
            })
          } else if (action.payload) {
            // Handle success case - cache the result and notify subscribers
            cachedResult[cacheKey] = action.payload

            subscribers.forEach(
              ({ dispatch, successType, successTypeMeta }) => {
                dispatch({
                  type: successType,
                  meta: successTypeMeta,
                  payload: action.payload,
                })
              },
            )
          }

          // Clear the pending request now that all subscribers have been notified
          // This happens for both success and error cases to prevent memory leaks
          delete pendingRequests[cacheKey]
        } else if (!error && action.payload) {
          // If no pending requests but we have a successful response, still cache it
          cachedResult[cacheKey] = action.payload
        }
      }
      return next(action)
    }

  /**
   * Clears the API cache and pending requests
   *
   * This function is used to clear the cache and pending requests when the
   * application is reloaded.
   *
   * @returns {void}
   */
  const clearApiCache = (): void => {
    Object.keys(cachedResult).forEach((key: string) => {
      delete cachedResult[key]
    })

    // Also clear any pending requests to prevent memory leaks
    Object.keys(pendingRequests).forEach((key: string) => {
      delete pendingRequests[key]
    })
  }

  // These are the middleware generated by this factory.
  // - `readCache` should go BEFORE the `apiMiddleware`
  // - `recordCache` should go AFTER the `apiMiddleware`
  // - `clearApiCache` is rarely called, it's complicated when to use
  return {
    readCache,
    recordCache,
    clearApiCache,
  }
}
